import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowRight, Zap, ShieldCheck, Smartphone, CheckCircle2 } from "lucide-react";
import { api, formatMoney } from "../lib/api";

const HERO = "https://images.unsplash.com/photo-1488998427799-e3362cec87c3?crop=entropy&cs=tinysrgb&fm=jpg&q=85&w=900";

const Kenya = () => {
  const [rate, setRate] = useState(null);
  useEffect(() => {
    api.get("/fx/rates").then(r => {
      const ke = r.data.rates.find(x => x.country_code === "KE");
      setRate(ke?.rate);
    });
  }, []);

  return (
    <div data-testid="kenya-landing">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 mudcloth-bg pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[var(--ra-border)] bg-white/80 text-xs uppercase tracking-[0.2em] text-[var(--ra-primary)] font-semibold mb-6">
              <span>🇰🇪</span> Kenya · M-Pesa instant
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-3">
              <span className="text-[var(--ra-accent)]">Karibu.</span><br />
              Tuma pesa nyumbani — <br className="hidden sm:block"/>
              <span className="text-[var(--ra-primary)]">kwa sekunde.</span>
            </h1>
            <p className="text-lg text-[var(--ra-muted)] max-w-xl mt-4 mb-8 leading-relaxed">
              Send money from anywhere to any M-Pesa number in Kenya — settled in seconds, not days.
              Great rates, zero fees, no surprises.
            </p>

            {rate && (
              <div className="ra-card p-5 mb-7 max-w-md" data-testid="kenya-rate-card">
                <div className="text-xs uppercase tracking-[0.2em] text-[var(--ra-muted)] font-semibold">Today&apos;s rate</div>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="font-heading text-2xl font-bold">1 USD</span>
                  <span className="text-[var(--ra-muted)]">=</span>
                  <span className="font-heading text-3xl font-bold text-[var(--ra-primary)]">{rate.toLocaleString()} KES</span>
                </div>
                <div className="text-xs text-[var(--ra-muted)] mt-1.5">Zero fees · Great rate</div>
              </div>
            )}

            <div className="flex flex-wrap gap-3">
              <Link to="/register"><Button size="lg" className="btn-primary rounded-md px-7 h-12" data-testid="kenya-cta-start">
                Send to M-Pesa now <ArrowRight size={16} className="ml-2" />
              </Button></Link>
              <Link to="/rates"><Button size="lg" variant="outline" className="rounded-md px-7 h-12 border-[var(--ra-primary)] text-[var(--ra-primary)]">
                See all rates
              </Button></Link>
            </div>
          </div>

          <div className="lg:col-span-5 fade-up fade-up-2">
            <div className="ra-card overflow-hidden p-0">
              <img src={HERO} alt="Mt. Kenya at sunrise" className="w-full aspect-[4/5] object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Why Kenyan diaspora chooses us */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="text-xs uppercase tracking-[0.2em] text-[var(--ra-accent)] font-semibold mb-3">Kwa nini ClearPurse?</div>
        <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-8 max-w-2xl">Made for the Kenyan diaspora.</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { icon: Smartphone, title: "Direct to M-Pesa", body: "Funds land in your recipient&apos;s phone in seconds. They withdraw at any agent." },
            { icon: Zap, title: "Settle in seconds", body: "Not days. Not &ldquo;1-3 business days&rdquo;. Actual seconds." },
            { icon: ShieldCheck, title: "Great FX rates", body: "Strong exchange rates — none of the hidden 2-4% markups banks pile on." },
            { icon: CheckCircle2, title: "Zero fees", body: "Send KES 100,000, pay KES 0 in fees. Always." },
          ].map(f => (
            <div key={f.title} className="ra-card p-5">
              <div className="w-10 h-10 rounded-lg bg-[var(--ra-primary)]/8 text-[var(--ra-primary)] flex items-center justify-center mb-4">
                <f.icon size={20} />
              </div>
              <h3 className="font-heading font-semibold mb-1.5">{f.title}</h3>
              <p className="text-sm text-[var(--ra-muted)] leading-relaxed" dangerouslySetInnerHTML={{ __html: f.body }} />
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-20">
        <div className="ra-card p-10 bg-[var(--ra-primary)] text-white border-[var(--ra-primary)]">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-3">Asante kwa kutembelea. Tuanze?</h2>
          <p className="text-white/80 max-w-md mb-6">Open a free account in 2 minutes. Every transfer is fee-free.</p>
          <Link to="/register"><Button size="lg" className="bg-[var(--ra-accent)] hover:bg-[#B85240] text-white rounded-md px-8 h-12" data-testid="kenya-cta-bottom">
            Create free account <ArrowRight size={16} className="ml-2" />
          </Button></Link>
        </div>
      </section>
    </div>
  );
};

export default Kenya;
